import { GameWorld } from "./GameWorld.tsx";
import { useGameAudio } from "./gameAudio.ts";
import { useEffect, useRef, useState } from "react";
import { GREENHOUSE_TARGETS, targetDistance } from "../shared/game.ts";
import { useLocale } from "./i18n.tsx";
import { GentleGameArena, GentleGameIntro, GentleGameResult } from "./GentleGameFrame.tsx";
import type { Session } from "./Shell.tsx";
import { useGentleGame } from "./useGentleGame.ts";

const TEXT = {
  "zh-CN": { eyebrow: "方向与回中", title: "窗边花房", lead: "轻轻转头，让蝴蝶接住阳光、雨滴和微风，再带回中央的小花。一次次照料，让新叶和花苞慢慢长出来。", preview: "一株植物在窗边迎接阳光和雨滴", center: "把照料带回中央的小花", cues: ["看看右侧的阳光", "轻轻抬头迎接雨滴", "看看左侧的微风", "看看右下方的新叶", "看看左下方的花苞", "轻轻抬头迎接晨光"], detail: (step: number, total: number, elapsed: number) => `第 ${step} 次照料，共 ${total} 次 · ${elapsed} 秒`, completeEyebrow: "花房苏醒", completeTitle: "一朵花开了", completeBody: (seconds: number) => `你用 ${seconds} 秒完成了六次轻柔照料。` },
  en: { eyebrow: "Move and return", title: "Window Greenhouse", lead: "Guide a butterfly with gentle head movements. Gather sunlight, rain, and breeze, then bring each gift back to your plant. Watch new leaves and a flower unfold.", preview: "A window plant welcoming sunlight and rain", center: "Bring your gift back to the plant at the centre", cues: ["Look toward the sunlight on the right", "Look gently up toward the rain", "Look toward the breeze on the left", "Look toward the new leaf below right", "Look toward the bud below left", "Look gently up toward the morning light"], detail: (step: number, total: number, elapsed: number) => `Care ${step} of ${total} · ${elapsed}s`, completeEyebrow: "The greenhouse wakes", completeTitle: "A flower has opened", completeBody: (seconds: number) => `You completed six gentle moments of care in ${seconds} seconds.` },
} as const;

const CENTER_TARGET = { id: "center", x: 0, y: 0, cue: "center" } as const;

export function GreenhouseGamePage({ session }: { session: Session | null }) {
  const { locale } = useLocale();
  const text = TEXT[locale];
  const runtime = useGentleGame("window_greenhouse", session);
  const audio = useGameAudio("greenhouse");
  const playableRuntime = { ...runtime, startCamera: async () => { audio.unlock(); await runtime.startCamera(); }, startManual: () => { audio.unlock(); runtime.startManual(); }, reset: () => { audio.stop(); runtime.reset(); } };
  const pointRef = useRef(runtime.point);
  pointRef.current = runtime.point;
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState<"target" | "center">("target");
  const [dwell, setDwell] = useState(0);
  const target = phase === "center" ? CENTER_TARGET : GREENHOUSE_TARGETS[step];
  const progress = Math.min(1, (step + (phase === "center" ? 0.5 : 0)) / GREENHOUSE_TARGETS.length);

  useEffect(() => {
    if (runtime.stage === "intro") { setStep(0); setPhase("target"); setDwell(0); }
  }, [runtime.stage]);

  useEffect(() => {
    if (runtime.stage !== "playing" || !target) return;
    let animation = 0;
    let last = performance.now();
    let accumulated = 0;
    let lastPainted = -1;
    const tick = (now: number) => {
      const delta = Math.min(100, now - last);
      last = now;
      if (pointRef.current && targetDistance(pointRef.current, target) <= (phase === "center" ? 0.24 : 0.28)) accumulated += delta;
      else accumulated = 0;
      const nextDwell = Math.min(1, accumulated / (phase === "center" ? 550 : 750));
      if (Math.abs(nextDwell - lastPainted) >= 0.03 || nextDwell === 0 || nextDwell === 1) { lastPainted = nextDwell; setDwell(nextDwell); }
      if (nextDwell >= 1) {
        setDwell(0);
        if (phase === "target") { audio.play("care"); setPhase("center"); }
        else if (step >= GREENHOUSE_TARGETS.length - 1) { audio.play("complete"); runtime.complete(); }
        else { audio.play("center"); setStep((value) => value + 1); setPhase("target"); }
        return;
      }
      animation = requestAnimationFrame(tick);
    };
    animation = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animation);
  }, [audio.play, phase, runtime.complete, runtime.stage, step, target]);

  const preview = <GameWorld game="greenhouse" step={4} cursor={{ x: .36, y: -.25 }} />;
  if (runtime.stage === "intro") return <GentleGameIntro runtime={playableRuntime} eyebrow={text.eyebrow} title={text.title} lead={text.lead} preview={preview} previewLabel={text.preview} tone="greenhouse" />;
  if (runtime.stage === "complete") return <GentleGameResult runtime={playableRuntime} eyebrow={text.completeEyebrow} title={text.completeTitle} body={text.completeBody(runtime.elapsed)} art={<GameWorld game="greenhouse" step={6} progress={1} />} tone="greenhouse" />;
  return <GentleGameArena runtime={playableRuntime} progress={progress} arenaLabel={text.preview} playingTitle={phase === "center" ? text.center : text.cues[step]} playingDetail={text.detail(step + 1, GREENHOUSE_TARGETS.length, runtime.elapsed)} className="greenhouse-game" controls={{ soundEnabled: audio.enabled, toggleSound: audio.toggle }}>
    <GameWorld game="greenhouse" cursor={runtime.cursor} step={step} dwell={dwell} returning={phase === "center"} progress={progress} active={runtime.stage === "playing"} onCourse={Boolean(runtime.point && targetDistance(runtime.point, target) <= .28)} />
  </GentleGameArena>;
}
